import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { FlowsIcon } from '../../../shared/ui/icons';
import { PanelFoot, SectionPanel } from '../../../shared/ui/section-panel';
import { ErrorState, LoadingState } from '../../../shared/ui/states';
import { listSourceTargets, usedInKeys } from '../../../entities/used-in';

interface IClientFlowsCardProps {
  sourceId: string;
  sourceName: string;
  clientName: string;
}

// Per-source, not per-client: nothing is routed by client, so the source's live flows are theirs.
export const ClientFlowsCard = ({ sourceId, sourceName, clientName }: IClientFlowsCardProps) => {
  const targets = useQuery({
    queryKey: usedInKeys.targets(sourceId),
    queryFn: () => listSourceTargets(sourceId),
  });
  const rows = targets.data ?? [];

  return (
    <SectionPanel title="Used in flows" note={`every flow that starts from ${sourceName}`}>
      {targets.isError && (
        <div className="p-card">
          <ErrorState error={targets.error} onRetry={() => void targets.refetch()} />
        </div>
      )}
      {targets.isPending && (
        <div className="p-card">
          <LoadingState rows={2} />
        </div>
      )}
      {targets.isSuccess && rows.length === 0 && (
        <p className="px-card py-4 text-meta text-muted">
          No flow starts from {sourceName} yet, so nothing runs on their requests.
        </p>
      )}
      {rows.map((flow) => (
        <Link
          key={flow.id}
          to="/flows/$flowId"
          params={{ flowId: flow.id }}
          className="flex items-center gap-inset border-b border-border-row px-card py-3 hover:bg-hover"
        >
          <span className="flex size-6 shrink-0 items-center justify-center rounded-item bg-chip-on text-muted">
            <FlowsIcon size={12} />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-data text-primary" title={flow.name}>
              {flow.name}
            </span>
            <span className="font-mono text-micro text-faint">
              {flow.paused ? 'paused' : 'live'}
            </span>
          </span>
        </Link>
      ))}
      {rows.length > 0 && (
        <PanelFoot>
          A request from {clientName} starts a run in each of these. Nothing here is set per client.
        </PanelFoot>
      )}
    </SectionPanel>
  );
};
